import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChartBar, ArrowLeft, Eye, PaperPlaneTilt, Download,
  ChatCircleDots, MagnifyingGlass, Spinner, ShieldCheck,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import LanguagePicker from "@/components/LanguagePicker";
import { useT } from "@/lib/i18n";

const EVENT_ICONS = {
  view: Eye, expand: Eye, share: PaperPlaneTilt,
  download: Download, search: MagnifyingGlass, feedback: ChatCircleDots,
};

const CATEGORY_COLORS = {
  warranty:   "bg-rose-50 text-rose-700 border-rose-200",
  pm_service: "bg-amber-50 text-amber-700 border-amber-200",
  sales:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  onboarding: "bg-violet-50 text-violet-700 border-violet-200",
  ui_bug:     "bg-orange-50 text-orange-700 border-orange-200",
  content:    "bg-slate-50 text-slate-700 border-slate-200",
  general:    "bg-slate-50 text-slate-600 border-slate-200",
};

/**
 * /admin/manual-stats — admin-only analytics for /help.
 *
 * Shows aggregate counts for the past 30 days plus the most recent feedback
 * messages with their AI-routed category + recipients. Read-only — uses the
 * existing GET endpoints from feedback_routes.py.
 */
export default function ManualStatsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const t = useT();
  const [stats, setStats] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("events");

  useEffect(() => {
    if (!user?.is_admin && !user?.is_super_admin) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [statsRes, feedbackRes] = await Promise.all([
          api.get("/admin/manual-stats"),
          api.get("/admin/manual-feedback?limit=50"),
        ]);
        if (!cancelled) {
          setStats(statsRes.data || {});
          setFeedback(feedbackRes.data?.items || []);
        }
      } catch {
        if (!cancelled) { setStats({}); setFeedback([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (user && !user.is_admin && !user.is_super_admin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="stats-access-denied">
        <div className="text-center">
          <ShieldCheck size={48} weight="duotone" className="mx-auto text-rose-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800">{t("manual_stats.admin_only", "Admin only")}</h1>
          <button onClick={() => navigate("/")} className="mt-3 text-sm text-blue-600 hover:underline">
            {t("manual_stats.back_home", "Back to home")}
          </button>
        </div>
      </div>
    );
  }

  // Sort events: highest count first
  const events = (stats?.events || []).slice().sort((a, b) => b.count - a.count);
  const fbByCat = stats?.feedback_by_category || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="stats-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")}
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500"
            data-testid="stats-back">
            <ArrowLeft size={18} weight="bold" />
          </button>
          <ChartBar size={20} weight="fill" className="text-blue-700" />
          <h1 className="text-base font-black tracking-tighter font-['Chivo']">{t("stats.title", "Manual Analytics")}</h1>
          <div className="ml-auto flex items-center gap-2">
            <LanguagePicker />
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <p className="text-sm text-slate-500 mb-6">{t("stats.subtitle", "Which tutorials get read, shared and downloaded.")}</p>

        {/* Top-line metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Metric label={t("stats.feedback_total", "Feedback received")} value={stats?.feedback_total ?? 0}
            icon={ChatCircleDots} testId="stats-metric-feedback" />
          <Metric label={t("stats.event_total", "Tracked events")} value={stats?.event_count ?? 0}
            icon={ChartBar} testId="stats-metric-events" />
          {fbByCat.slice(0, 2).map((c) => (
            <Metric key={c.category} label={c.category.replace("_", " ").toUpperCase()}
              value={c.count} icon={ChatCircleDots} testId={`stats-cat-${c.category}`} />
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-3 border-b border-slate-200">
          {[
            ["events", t("stats.tab_events", "Events")],
            ["feedback", t("stats.tab_feedback", "By category")],
            ["inbox", t("stats.tab_inbox", "Recent feedback")],
          ].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              data-testid={`stats-tab-${key}`}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === key ? "border-b-2 border-blue-600 text-blue-700" : "text-slate-500 hover:text-slate-800"
              }`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading…</div>
        ) : tab === "events" ? (
          events.length === 0 ? (
            <p className="text-sm text-slate-500 italic">{t("stats.no_data", "No data yet.")}</p>
          ) : (
            <table className="w-full text-sm" data-testid="stats-events-table">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 px-3">{t("table.role", "Role")}</th>
                  <th className="text-left py-2 px-3">{t("table.section", "Section")}</th>
                  <th className="text-left py-2 px-3">{t("table.event", "Event")}</th>
                  <th className="text-right py-2 px-3">{t("table.count", "Count")}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => {
                  const Icon = EVENT_ICONS[e.event_type] || Eye;
                  return (
                    <tr key={i} className="border-b border-slate-100 hover:bg-white" data-testid={`stats-event-row-${i}`}>
                      <td className="py-2 px-3 font-mono text-xs">{e.role || "—"}</td>
                      <td className="py-2 px-3">{e.section_id || <span className="text-slate-400 italic">{t("manual_stats.whole_role", "whole role")}</span>}</td>
                      <td className="py-2 px-3">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <Icon size={12} weight="bold" className="text-slate-500" />
                          {e.event_type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right font-bold">{e.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : tab === "feedback" ? (
          fbByCat.length === 0 ? (
            <p className="text-sm text-slate-500 italic">{t("manual_stats.no_feedback_yet", "No feedback yet.")}</p>
          ) : (
            <div className="space-y-2" data-testid="stats-feedback-categories">
              {fbByCat.map((c) => {
                const max = Math.max(...fbByCat.map((x) => x.count));
                const width = max > 0 ? (c.count / max) * 100 : 0;
                return (
                  <div key={c.category} className="flex items-center gap-3" data-testid={`stats-bar-${c.category}`}>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${CATEGORY_COLORS[c.category] || CATEGORY_COLORS.general} w-32 shrink-0 text-center`}>
                      {c.category.replace("_", " ")}
                    </span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-sm overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${width}%` }} />
                    </div>
                    <span className="text-sm font-bold w-12 text-right">{c.count}</span>
                  </div>
                );
              })}
            </div>
          )
        ) : (
          // Recent feedback inbox
          feedback.length === 0 ? (
            <p className="text-sm text-slate-500 italic">{t("manual_stats.no_feedback_yet", "No feedback yet.")}</p>
          ) : (
            <ul className="space-y-3" data-testid="stats-feedback-list">
              {feedback.map((f) => (
                <li key={f.feedback_id} className="bg-white rounded-md border border-slate-200 p-4" data-testid={`stats-feedback-${f.feedback_id}`}>
                  <div className="flex items-start gap-2 mb-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${CATEGORY_COLORS[f.category] || CATEGORY_COLORS.general}`}>
                      {f.category}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">{f.role_context}</span>
                    {f.section_id && <span className="text-[10px] font-mono text-slate-400">· {f.section_id}</span>}
                    <span className="text-[10px] font-mono text-slate-400">· {f.lang?.toUpperCase()}</span>
                    <span className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm ${
                      f.email_status === "delivered" ? "bg-emerald-100 text-emerald-700" :
                      f.email_status === "partial" ? "bg-amber-100 text-amber-700" :
                      "bg-slate-100 text-slate-500"
                    }`}>{f.email_status}</span>
                  </div>
                  <p className="text-sm text-slate-800 mb-2">{f.message}</p>
                  <div className="text-xs text-slate-500">
                    <strong>{t("manual_stats.from_label", "From:")}</strong> <span className="font-mono">{f.sender_email}</span>
                    {" · "}<strong>{t("manual_stats.routed_to", "Routed to:")}</strong>{" "}
                    {(f.recipients || []).join(", ") || <em>{t("manual_stats.none_matched", "none matched")}</em>}
                  </div>
                  {f.ai_rationale && (
                    <p className="text-[11px] text-slate-400 mt-1.5 italic">
                      AI ({f.ai_source}, {Math.round((f.ai_confidence || 0) * 100)}%): {f.ai_rationale}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )
        )}
      </main>
    </div>
  );
}

function Metric({ label, value, icon: Icon, testId }) {
  return (
    <div className="bg-white rounded-md border border-slate-200 p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="text-2xl font-black text-slate-900 font-['Chivo']">{value}</div>
    </div>
  );
}
